"use client";

import {
  GUEST_DEMO_MINUTE_FIVE_CHOICE_EXPERIMENT_KEY,
  GUEST_DEMO_MINUTE_FIVE_CHOICE_VARIANTS,
  type GuestDemoMinuteFiveChoiceAssignment,
  getOrCreateGuestDemoMinuteFiveChoiceAssignment,
} from "@/lib/experiments/guestDemoMinuteFiveChoice";
import { getGuestSpatiusAvatarExposureProperties } from "@/lib/experiments/guestSpatiusAvatar";
import { type FreemiumCohort, getFreemiumDiscountCoupon } from "@/lib/freemium/usage-state";
import { stageLatestGuestSpeakingSessionForClaim } from "@/lib/guestSessionClaim";
import { buildTrackedPaywallUrl } from "@/lib/paywallUrl";
import { X } from "lucide-react";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import { trackEventWithAttribution, trackUpgradeIntent } from "../lib/analytics/posthog-helpers";
import { getAttributionData } from "../lib/analytics/utm-tracker";
import { type GuestDemoChoiceDismissMethod, GuestDemoChoiceGate } from "./GuestDemoChoiceGate";

export type TimeLimitPopupLimitType = "guest" | "daily" | "weekly";
export type TimeLimitPopupCloseAction = "dismiss" | "signup" | "trial";

interface TimeLimitPopupProps {
  onClose: (action?: TimeLimitPopupCloseAction) => void;
  userEmail?: string | null;
  variant?: "default" | "zen";
  limitType?: TimeLimitPopupLimitType;
  freemiumCohort?: FreemiumCohort;
  qualifiedConversationCount?: number;
  qualifiedConversationsThisWeek?: number;
  guestChoiceAssignmentOverride?: GuestDemoMinuteFiveChoiceAssignment;
}

const TimeLimitPopup: React.FC<TimeLimitPopupProps> = ({
  onClose,
  userEmail,
  variant = "default",
  limitType = userEmail ? "daily" : "guest",
  freemiumCohort,
  qualifiedConversationCount,
  qualifiedConversationsThisWeek,
  guestChoiceAssignmentOverride,
}) => {
  const router = useRouter();
  const posthog = usePostHog();
  const discountCoupon = getFreemiumDiscountCoupon(freemiumCohort);
  const shouldAssignGuestChoice = !userEmail && limitType === "guest" && variant === "default";
  const [guestChoiceAssignment, setGuestChoiceAssignment] =
    useState<GuestDemoMinuteFiveChoiceAssignment | null>(null);
  const hasTrackedGuestChoiceExposureRef = useRef(false);
  const resolvedGuestChoiceAssignment = guestChoiceAssignmentOverride ?? guestChoiceAssignment;

  useEffect(() => {
    if (!shouldAssignGuestChoice || guestChoiceAssignmentOverride) return;
    setGuestChoiceAssignment(getOrCreateGuestDemoMinuteFiveChoiceAssignment());
  }, [guestChoiceAssignmentOverride, shouldAssignGuestChoice]);

  useEffect(() => {
    if (
      !shouldAssignGuestChoice ||
      !resolvedGuestChoiceAssignment ||
      hasTrackedGuestChoiceExposureRef.current
    ) {
      return;
    }

    hasTrackedGuestChoiceExposureRef.current = true;
    trackEventWithAttribution(posthog, "guest_demo_choice_exposed", {
      experiment_key: GUEST_DEMO_MINUTE_FIVE_CHOICE_EXPERIMENT_KEY,
      experiment_variant: resolvedGuestChoiceAssignment.variant,
      assignment_id: resolvedGuestChoiceAssignment.assignmentId,
      limit_type: "guest",
      source: "time_limit_popup",
    });
  }, [posthog, resolvedGuestChoiceAssignment, shouldAssignGuestChoice]);

  const trackGuestChoiceSelection = (
    choice: "trial" | "signup_free" | "not_now",
    dismissalMethod?: GuestDemoChoiceDismissMethod
  ) => {
    if (!resolvedGuestChoiceAssignment) return;

    trackEventWithAttribution(posthog, "guest_demo_choice_selected", {
      experiment_key: GUEST_DEMO_MINUTE_FIVE_CHOICE_EXPERIMENT_KEY,
      experiment_variant: resolvedGuestChoiceAssignment.variant,
      assignment_id: resolvedGuestChoiceAssignment.assignmentId,
      choice,
      dismissal_method: dismissalMethod,
      limit_type: "guest",
      source: "time_limit_popup",
    });
  };

  const handleDismiss = () => {
    trackEventWithAttribution(posthog, "time_limit_prompt_dismissed", {
      limit_type: limitType,
      freemium_cohort: freemiumCohort,
      qualified_conversation_count: qualifiedConversationCount,
      qualified_conversations_this_week: qualifiedConversationsThisWeek,
      source: "time_limit_popup",
      user_type: userEmail ? "authenticated" : "guest",
    });
    onClose("dismiss");
  };

  const handleSignUp = () => {
    stageLatestGuestSpeakingSessionForClaim();
    const attributionData = getAttributionData();
    const params = new URLSearchParams();
    const redirect =
      typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "/";

    params.set("redirect", redirect || "/");
    if (
      resolvedGuestChoiceAssignment?.variant ===
      GUEST_DEMO_MINUTE_FIVE_CHOICE_VARIANTS.combinedChoice
    ) {
      params.set("intent", "free_continue");
      trackGuestChoiceSelection("signup_free");
    }
    if (attributionData?.utm_source) params.set("utm_source", attributionData.utm_source);
    if (attributionData?.utm_medium) params.set("utm_medium", attributionData.utm_medium);
    if (attributionData?.utm_campaign) params.set("utm_campaign", attributionData.utm_campaign);
    if (attributionData?.utm_term) params.set("utm_term", attributionData.utm_term);
    if (attributionData?.utm_content) params.set("utm_content", attributionData.utm_content);
    if (attributionData?.gclid) params.set("gclid", attributionData.gclid);

    if (variant === "default") {
      posthog?.capture("fullscreen_guest_signup_clicked", {
        source: "time_limit_popup",
        redirect,
        ...getGuestSpatiusAvatarExposureProperties(),
      });
    }

    router.push(`/auth?${params.toString()}`);
    onClose("signup"); // Close popup after redirecting
  };

  const handleTrial = () => {
    if (!resolvedGuestChoiceAssignment) return;
    stageLatestGuestSpeakingSessionForClaim();

    const returnTo =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : "/";
    const paywallUrl = buildTrackedPaywallUrl({
      extraParams: {
        assignment_id: resolvedGuestChoiceAssignment.assignmentId,
        conversion_origin: "minute_five_choice",
        experiment_key: GUEST_DEMO_MINUTE_FIVE_CHOICE_EXPERIMENT_KEY,
        experiment_variant: resolvedGuestChoiceAssignment.variant,
        preferred_package: "monthly",
      },
      returnTo,
      source: "guest_demo_minute_five_choice",
    });
    const authParams = new URLSearchParams({
      intent: "trial",
      redirect: paywallUrl,
    });

    trackGuestChoiceSelection("trial");
    router.push(`/auth?${authParams.toString()}`);
    onClose("trial");
  };

  const handleGuestChoiceDismiss = (dismissalMethod: GuestDemoChoiceDismissMethod) => {
    trackGuestChoiceSelection("not_now", dismissalMethod);
    onClose("dismiss");
  };

  const handleUpgrade = () => {
    if (!userEmail) return;
    trackUpgradeIntent(posthog, "time_limit_popup", {
      email: userEmail,
      limit_type: limitType,
      freemium_cohort: freemiumCohort,
      qualified_conversation_count: qualifiedConversationCount,
      qualified_conversations_this_week: qualifiedConversationsThisWeek,
      offer_mode: discountCoupon ? "discount" : "trial",
      coupon_code: discountCoupon || undefined,
    });

    // Use Next.js router for instant client-side navigation
    router.push(
      buildTrackedPaywallUrl({
        coupon: discountCoupon,
        extraParams: { limit_type: limitType },
        mode: variant === "zen" ? "zen" : undefined,
        source: "time_limit_popup",
      })
    );
  };

  if (shouldAssignGuestChoice && !resolvedGuestChoiceAssignment) {
    return null;
  }

  if (
    shouldAssignGuestChoice &&
    resolvedGuestChoiceAssignment?.variant === GUEST_DEMO_MINUTE_FIVE_CHOICE_VARIANTS.combinedChoice
  ) {
    return (
      <GuestDemoChoiceGate
        onChooseFree={handleSignUp}
        onChooseTrial={handleTrial}
        onDismiss={handleGuestChoiceDismiss}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`${
          variant === "zen" ? "bg-stone-900 border border-stone-700/50" : "bg-neutral-800"
        } p-6 rounded-lg shadow-xl max-w-sm w-full text-white`}
      >
        <div className="flex justify-between items-center mb-4">
          <h2
            className={`text-xl font-semibold ${variant === "zen" ? "font-serif tracking-wide text-stone-200" : ""}`}
          >
            {variant === "zen" ? "Session Concluded" : "Session Time Limit Reached"}
          </h2>
          <button
            onClick={handleDismiss}
            className="text-neutral-400 hover:text-white transition-colors"
            aria-label="Close popup"
          >
            <X size={24} />
          </button>
        </div>
        {userEmail ? (
          <>
            <p
              className={`mb-6 ${variant === "zen" ? "text-stone-300 font-light" : "text-neutral-300"}`}
            >
              {limitType === "weekly"
                ? discountCoupon
                  ? "You've used your 3 free speaking sessions this week. You've built real speaking momentum, and today's offer unlocks unlimited practice without weekly limits."
                  : variant === "zen"
                    ? "You've used your free Zen sessions this week. Subscribe to keep practicing without weekly limits."
                    : "You've used your 3 free speaking sessions this week. Subscribe to keep practicing without weekly limits."
                : limitType === "daily"
                  ? discountCoupon
                    ? "You've used today's 10 minutes of free speaking practice. You've built real speaking momentum, and today's offer unlocks unlimited practice."
                    : variant === "zen"
                      ? "Your daily practice limit has been reached. Subscribe to unlock unlimited Zen sessions and continue your journey."
                      : "You've used today's 10 minutes of free speaking practice. Subscribe to unlock unlimited sessions."
                  : variant === "zen"
                    ? "Your journey pauses here. Sign up to extend your practice, or subscribe for unlimited access."
                    : "Your free demo is finished. Create a free account to keep practicing and save progress from your next session."}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDismiss}
                className={`px-4 py-2 rounded-md transition-colors text-white ${
                  variant === "zen"
                    ? "bg-stone-800 hover:bg-stone-700 font-light"
                    : "bg-neutral-700 hover:bg-neutral-600"
                }`}
              >
                Dismiss
              </button>
              <button
                onClick={handleUpgrade}
                className={`px-4 py-2 rounded-md transition-colors text-white font-semibold ${
                  variant === "zen"
                    ? "bg-orange-700/80 hover:bg-orange-600/80 border border-orange-500/30"
                    : "bg-orange-600 hover:bg-orange-500"
                }`}
              >
                {discountCoupon
                  ? "Unlock unlimited practice"
                  : variant === "zen"
                    ? "Expand Process"
                    : "Upgrade to PRO"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p
              className={`mb-6 ${variant === "zen" ? "text-stone-300 font-light" : "text-neutral-300"}`}
            >
              {variant === "zen"
                ? "Your journey pauses here. Sign up to extend your practice, or subscribe for unlimited access."
                : "Your free demo is finished. Create a free account to keep practicing and save progress from your next session."}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDismiss}
                className={`px-4 py-2 rounded-md transition-colors text-white ${
                  variant === "zen"
                    ? "bg-stone-800 hover:bg-stone-700 font-light"
                    : "bg-neutral-700 hover:bg-neutral-600"
                }`}
              >
                Dismiss
              </button>
              <button
                onClick={handleSignUp}
                className={`px-4 py-2 rounded-md transition-colors text-white font-semibold ${
                  variant === "zen"
                    ? "bg-stone-700 hover:bg-stone-600 border border-stone-500/30"
                    : "bg-blue-600 hover:bg-blue-500"
                }`}
              >
                {variant === "zen" ? "Join Journey" : "Sign Up Free"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TimeLimitPopup;
